import Ticket from "../interfaces/ticket/Ticket";

export interface StatusDefinition {
    name: string;
    weight: number;
    column: string;
}

// This is now the single source of truth for status order and grouping.
export const STATUSES: StatusDefinition[] = [
    { name: 'Open', weight: 1, column: 'Open' },
    { name: 'In Progress', weight: 2, column: 'In Progress' },
    { name: 'In Review', weight: 3, column: 'QA' },
    { name: 'Ready for QA', weight: 4, column: 'QA' },
    { name: 'Re-Opened', weight: 5, column: 'In Progress' },
    { name: 'Awaiting Release', weight: 6, column: 'Awaiting Release' },
    { name: 'Done', weight: 7, column: 'Done' },
];

// Defines the order of columns on the board.
export const BOARD_COLUMNS = ['Open', 'In Progress', 'QA', 'Awaiting Release', 'Done'];

/**
 * Gets all status definitions for a given column, sorted by their weight.
 * @param columnName The name of the board column.
 * @returns An array of StatusDefinition objects.
 */
export const getStatusesForColumn = (columnName: string): StatusDefinition[] => {
    return STATUSES
        .filter(status => status.column === columnName)
        .sort((a, b) => a.weight - b.weight);
};

/**
 * Gets all tickets that belong in a specific column.
 * @param columnName The name of the board column.
 * @param tickets The list of all tickets.
 * @returns A filtered array of tickets.
 */
export const getTicketsForColumn = (columnName: string, tickets: Ticket[]): Ticket[] => {
    const statusesInColumn = getStatusesForColumn(columnName).map(s => s.name);
    return tickets.filter(ticket => ticket.status && statusesInColumn.includes(ticket.status));
};

/**
 * Returns all statuses sorted by their defined weight.
 */
export const getSortedStatuses = (): StatusDefinition[] => {
    return [...STATUSES].sort((a, b) => a.weight - b.weight);
};

// Define the type for the status names and ensure all keys are strings.
// This interface can be useful for typing functions that accept a status string.
export type TicketStatus = 
    | "Open"
    | "In Progress"
    | "In Review"
    | "Awaiting Release"
    | "Done"
    | "Ready for QA"
    | "Re-Opened";

// Define the structure for our color map: a record where the key is the status
// and the value is the corresponding hex color code (string).
export interface StatusColorMap {
    [key: string]: string;
}

/**
 * @constant STATUS_COLOR_MAP
 * @description Provides a mapping of ticket status names to their designated hex color codes.
 * The colors are chosen based on the following convention:
 * - Gray/Neutral: Lowest priority (Open).
 * - Blue: Active work (In Progress).
 * - Amber/Orange: Warning/Urgency (Re-Opened).
 * - Purple: Review/Inspection/Testing stages (In Review, Ready for QA).
 * - Teal/Green: Final staging/holding (Awaiting Release).
 * - Dark Gray: Completed (Done).
 */
export const STATUS_COLOR_MAP: StatusColorMap = {
    // 1. Open: Neutral/Default, low visual priority.
    "Open": "#BDC3C7", // Light Gray / Silver
    
    // 2. In Progress: Primary active work color, highly visible.
    "In Progress": "#3498DB", // Bright Blue
    
    // 3. In Review & 6. Ready for QA: Combined into a single distinctive inspection/testing color.
    "In Review": "#9B59B6", // Purple
    "Ready for QA": "#9B59B6", // Purple
    
    // 4. Awaiting Release: Final step before Done, using a calm, secondary success color.
    "Awaiting Release": "#1ABC9C", // Teal
    
    // 5. Done: Subdued, archive color.
    "Done": "#2C3E50", // Dark Slate Gray
    
    // 7. Re-Opened: Warning/Alert, needs attention but not a hard stop like red.
    "Re-Opened": "#F39C12", // Amber / Orange
};
